import { CompositeFilterDescriptor, FilterDescriptor } from "@progress/kendo-data-query";

const DATE_FIELDS = ["performedOn"];

const startOfDay = (d: Date): string =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
const startOfNextDay = (d: Date): string =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 1)).toISOString();

// Converts Date objects on date fields to ISO strings.
// Expands "eq" into gte(startOfDay) + lt(startOfNextDay) within the same group.
export const normalizeDateFilters = (composite: CompositeFilterDescriptor): CompositeFilterDescriptor => {
  const normalized: (FilterDescriptor | CompositeFilterDescriptor)[] = [];

  for (const f of composite.filters) {
    if ("filters" in f) {
      normalized.push(normalizeDateFilters(f as CompositeFilterDescriptor));
      continue;
    }
    const fd = f as FilterDescriptor;
    if (!DATE_FIELDS.includes(fd.field as string) || !(fd.value instanceof Date)) {
      normalized.push(fd);
      continue;
    }
    const d = fd.value as Date;
    if (fd.operator === "eq") {
      normalized.push({ field: fd.field, operator: "gte", value: startOfDay(d) } as FilterDescriptor);
      normalized.push({ field: fd.field, operator: "lt",  value: startOfNextDay(d) } as FilterDescriptor);
    } else if (fd.operator === "gte" || fd.operator === "gt") {
      normalized.push({ ...fd, value: startOfDay(d) });
    } else if (fd.operator === "lt" || fd.operator === "lte") {
      normalized.push({ ...fd, value: startOfNextDay(d) });
    } else {
      normalized.push(fd);
    }
  }

  return { ...composite, filters: normalized };
};

export const parseKendoFilter = (kendoFilter: any) => {
  let filterString = "";
  if (kendoFilter && kendoFilter.filter) {
    filterString = parseKendoLogicalFilter(kendoFilter.filter);
  }
  return filterString;
};

const parseKendoLogicalFilter = (filter: any) => {
  let logicalFilter = "";

  if(!filter?.filters?.length) return "";

  if (filter && filter.filters && filter.filters.length > 0) {
    for (let i = 0; i < filter.filters.length; i++) {
      const currentFilter = filter.filters[i];
      if (currentFilter.logic && currentFilter.filters && currentFilter.filters.length > 0) {
        const subFilterString = parseKendoLogicalFilter(currentFilter);
        logicalFilter += `${subFilterString}`;
      } else if (currentFilter.field && currentFilter.operator) {
        logicalFilter += getQuery(currentFilter);
      }
      if (i < filter.filters.length - 1) {
        logicalFilter += ` ${filter.logic} `;
      }
    }
  }

  return logicalFilter;
}

const getQuery = (query: any) => {
  const operator = query.operator;
  const typeOfValue = typeof (query.value);

  if (query.field === "StartDate" ||
    query.field === "EndDate" ||
    query.field === "LicenseExpiryDate" ||
    query.field === "CancellationFormDate" ||
    query.field === "ILOEStartDate" ||
    query.field === "ILOEEndDate" ||
    query.field === "CancellationFormDate" ||
    query.field === "CancellationDate" ||
    query.field === "EmployeeDepartureDate" ||
    query.field === "AbscondingDate" ||
    query.field === "AbscondingDateForMohre" ||
    query.field === "AbscondingApprovalDate" ||
    query.field === "AbscondingArrivalDate" ||
    query.field === "TawjeehTrainingDate" ||
    query.field === "MohrePaymentDate" ||
    query.field === "ContractPaymentDate" ||
    query.field === "PassportIssueDate" ||
    query.field === "PassportExpiryDate" ||
    query.field === "VisaIssueDate" ||
    query.field === "VisaExpiryDate" ||
    query.field === "GovtIdIssueDate" ||
    query.field === "GovtIdExpiryDate" ||
    query.field === "WorkPermitIssueDate" ||
    query.field === "WorkPermitExpiryDate" ||
    query.field === "LaborCardIssueDate" ||
    query.field === "LaborCardExpiryDate") {

    const parsedDate = new Date(query.value);
    const isoDateString = parsedDate.toISOString();
    switch (operator) {
      case "eq":
        return `${query.field} eq ${isoDateString}`;
      case "neq":
        return `${query.field} neq ${isoDateString}`;
      case "gte":
        return `${query.field} gte ${isoDateString}`;
      case "gt":
        return `${query.field} gt ${isoDateString}`;
      case "lt":
        return `${query.field} lt ${isoDateString}`;
      case "lte":
        return `${query.field} lte ${isoDateString}`;
    }
  }
  const value = typeOfValue === "number" || typeOfValue === "boolean" ? query.value : `'${query.value}'`
  switch (operator) {
    case "contains":
      return `contains(${query.field}, ${value})`;
    case "doesnotcontain":
      return `not(contains(${query.field}, ${value})`;

    case "startswith":
      return `startsWith(${query.field}, ${value})`;
    case "eq":
      return `${query.field} eq ${value}`;
    case "neq":
      return `not(${query.field} eq ${value})`;
    default:
      return "";
  }
}
